import type { ObjectId } from 'mongodb';

import { GeothermalManufacturer } from './ApprovedVendors';
import type { AttestmentData, DomesticContentAttestmentData } from './Attestment';
import type { DCQualificationResponse, ITCQualificationResponse } from './DomesticContentTypes';
import { EquipmentType } from './EnergyBackup';
import { ProgramType } from './ProgramTypes';
import { HardwareType } from './Sundial';
import type { UserInfo } from './UserTypes';

export enum PanelOrientation {
  landscape = 'landscape',
  portrait = 'portrait',
  combination = 'combination',
}

export enum MountingType {
  ground = 'ground',
  roof = 'roof',
  combination = 'combination',
}

export interface MonthlyProduction {
  month: number;
  productionKwh: number;
}

export interface MonthlySolarAccess {
  month: number;
  solarAccess: number;
}

export enum SystemAssetType {
  inverter = 'inverter',
  battery = 'battery',
  panel = 'panel',
}

export interface SystemAsset {
  manufacturer: string;
  model: string;
  count: number;
  vintage?: string;
  avlItemId?: string;
  /** @deprecated Use SystemDesign.hardwareLookup values instead */
  hardwareDetails?: {
    serialNumber: string;
    sku: string;
  }[];
}

export type SystemAssetWithTypeAndWarranty = SystemAsset & {
  type: SystemAssetType;
  extendedWarrantyCost: number;
};

export interface HardwareDetail {
  serialNumbers: string[];
  sku: string | null;
  count: number;
}

export interface SystemDesignHardwareLookup {
  details: HardwareDetail[];
  hardwareType: HardwareType;
  /** @deprecated Count now lives at the "details" level */
  count?: number;
}

export interface SystemArray {
  panelManufacturer?: string;
  panelModel?: string;
  panelAvlItemId?: string;
  panelCount: number;
  panelWattage?: number;
  /** @deprecated */
  orientation?: PanelOrientation | null;
  tilt: number;
  tiltUnit?: 'degrees' | 'radians';
  azimuth: number;
  azimuthUnit?: 'degrees' | 'radians';
  annualTSRF: number;
  annualTSRFUnit?: 'decimal' | 'percent';
  size: number;
  monthlySolarAccess: {
    month: number;
    accessPercent: number;
  }[];
  monthlySAVUnit?: 'decimal' | 'percent';
  inverter: number; // the inverter index
}

export interface ToleranceException {
  note?: string;
  exceptionGivenBy: string;
}

/** @deprecated */
interface SystemDesignDocumentDeprecated {
  /** @deprecated */
  inverterManufacturer?: string;
  /** @deprecated */
  inverterModel?: string;
  /** @deprecated */
  inverterCount?: number;
  /** @deprecated */
  mountingModel?: string;
  /** @deprecated */
  totalMountingCount?: number;
  /** @deprecated */
  storageManufacturer?: string;
  /** @deprecated */
  storageModel?: string;
  /** @deprecated */
  totalStorageCount?: number;
}

/** @deprecated
 * Please use account.electricityUsageKwh
 */
export interface AnnualConsumption {
  month: number;
  consumption: number;
}

export interface SystemDesign extends SystemDesignDocumentDeprecated {
  id?: ObjectId;
  accountId: ObjectId;
  isCurrent: boolean;
  isApproved?: boolean;
  systemFirstYearProductionKwh: number;
  systemSizeKw: number;
  asBuiltSystemSizeKw?: number;
  inverters?: Partial<SystemAsset>[] | [];
  panelManufacturer?: string;
  panelModel?: string;
  totalPanelCount?: number;
  arrays?: Partial<SystemArray>[] | [];
  firstYearMonthlyProduction?: Partial<MonthlyProduction>[] | [];
  mountingType?: string;
  storage?: Partial<SystemAsset>[] | [];
  totalStorageCapacityKwh?: number;
  inverterManufacturer?: string;
  inverterModel?: string;
  inverterCount?: number;
  firstYearMonthlyProductionKwh?: Array<Partial<MonthlyProduction>> | [];
  mountingManufacturer?: string;
  dateCreated: Date;
  toleranceException?: ToleranceException | null;
  attestment?: AttestmentData | null;
  domesticContentAttestment?: DomesticContentAttestmentData | null;
  /** @deprecated Use itcQualification instead */
  domesticContentQualification?: {
    result: DCQualificationResponse;
    override?: {
      user: UserInfo;
      date: Date;
    };
  };
  mountingHardware?: MountingHardware[] | [];
  /** @deprecated
   * Use account.electricityUsageKwh
   */
  annualConsumption?: AnnualConsumption[] | [];
  itcQualification?: {
    result: ITCQualificationResponse;
    override?: {
      user: UserInfo;
      date: Date;
    };
  };
  hardwareLookup?: SystemDesignHardwareLookup[] | null;
  enphaseInverterAttested?: boolean;
  enphaseStorageAttested?: boolean;
}

export interface MountingHardware {
  type: DomesticContentMountingHardwareType;
  mountingHardwareModels: MountingHardwareSelectedOption[];
}

export interface MountingHardwareSelectedOption {
  value?: string;
  label?: string;
  manufacturer: string;
  category: string;
  sku: string;
  avlItemId?: string;
}

export const QCELLS_AC_MANUFACTURER_NAME = 'QCells';
export const QCELLS_AC_MODEL_SUBSTRING = '/AC';

export function isQCellsAcModule(panelManufacturer?: string | null, panelModel?: string | null): boolean {
  return panelManufacturer === QCELLS_AC_MANUFACTURER_NAME && !!panelModel?.includes(QCELLS_AC_MODEL_SUBSTRING);
}

export enum HVACEquipmentType {
  heatPump = 'heatPump',
  airHandler = 'airHandler',
  heatStrip = 'heatStrip',
  furnace = 'furnace',
  evaporatorCoil = 'evaporatorCoil',
  ductlessOutdoor = 'ductlessOutdoor',
  ductlessIndoor = 'ductlessIndoor',
  airConditioner = 'airConditioner',
  packagedAC = 'packagedAC',
  packagedHeatPump = 'packagedHeatPump',
  packagedGasAC = 'packagedGasAC',
  packagedDualFuel = 'packagedDualFuel',
  other = 'other',
}

export enum HVACAccessoryType {
  thermostat = 'thermostat',
  airFilter = 'airFilter',
  dehumidifier = 'dehumidifier',
  humidifier = 'humidifier',
  electronicAirCleaner = 'electronicAirCleaner',
  uvSystem = 'uvSystem',
  zoning = 'zoning',
}

export enum ZoningType {
  zoneBoard = 'zoneBoard',
  zoneDamper = 'zoneDamper',
  bypassDamper = 'bypassDamper',
}

export enum HVACAirFilterType {
  mediaFilter = 'mediaFilter',
  standardFilter = 'standardFilter',
}

export const HVACAirFilterTypeLabels: Record<HVACAirFilterType, string> = {
  [HVACAirFilterType.mediaFilter]: 'Media Filter',
  [HVACAirFilterType.standardFilter]: 'Standard 1" Filter',
} as const;

export const HVACEquipmentTypeLabels: Record<HVACEquipmentType, string> = {
  [HVACEquipmentType.airHandler]: 'Air Handler',
  [HVACEquipmentType.heatPump]: 'Heat Pump',
  [HVACEquipmentType.heatStrip]: 'Heat Strip',
  [HVACEquipmentType.furnace]: 'Furnace',
  [HVACEquipmentType.evaporatorCoil]: 'Evaporator Coil',
  [HVACEquipmentType.ductlessOutdoor]: 'Outdoor Unit',
  [HVACEquipmentType.ductlessIndoor]: 'Indoor Unit',
  [HVACEquipmentType.airConditioner]: 'Air Conditioner',
  [HVACEquipmentType.packagedAC]: 'Packaged AC',
  [HVACEquipmentType.packagedHeatPump]: 'Packaged Heat Pump',
  [HVACEquipmentType.packagedGasAC]: 'Packaged Gas AC',
  [HVACEquipmentType.packagedDualFuel]: 'Packaged Dual Fuel',
  [HVACEquipmentType.other]: 'Other',
} as const;

export const HVACAccessoryLabels: Record<HVACAccessoryType | ZoningType, string> = {
  [HVACAccessoryType.thermostat]: 'Thermostat',
  [HVACAccessoryType.airFilter]: 'Air Filter',
  [HVACAccessoryType.dehumidifier]: 'Dehumidifier',
  [HVACAccessoryType.humidifier]: 'Bypass/Fan Powered Humidifier',
  [HVACAccessoryType.electronicAirCleaner]: 'Electronic Air Cleaner',
  [HVACAccessoryType.uvSystem]: 'UV Light',
  [HVACAccessoryType.zoning]: 'Zoning',
  [ZoningType.zoneBoard]: 'Zone Board',
  [ZoningType.zoneDamper]: 'Zone Damper',
  [ZoningType.bypassDamper]: 'Bypass Damper (optional)',
} as const;

/** @deprecated */
export interface LegacyHVACEquipmentDetails {
  type: HVACEquipmentType;
  name: string;
  manufacturer: string;
  model: string;
  quantity: number;
  cost?: number;
  productId?: string;
  serialNumbers?: string[];
}

/** @deprecated */
export interface LegacyHVACInstallKitDetails {
  name: string;
  quantity: number;
  cost?: number;
}

/** @deprecated */
export interface LegacyHVACServiceDetails {
  name: string;
  cost?: number;
  quantity: number;
}

/** @deprecated */
export interface HVACDiscountDetails {
  name: string;
  cost: number;
}

/** @deprecated */
export interface LegacyHVACSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
  equipment: LegacyHVACEquipmentDetails[];
  installKits?: LegacyHVACInstallKitDetails[];
  services?: LegacyHVACServiceDetails[];
  discounts?: HVACDiscountDetails[];
  ahriNumber?: string;
}

export interface HVACEquipmentItem {
  type: HVACEquipmentType;
  name: string;
  manufacturer: string;
  model: string;
  quantity: number;
  productId?: string;
  serialNumbers?: string[];
  size?: { value: string; unit: string };
  efficiencies?: { value: string; unit: string }[];
}

export interface HVACPreliminaryEquipmentItem {
  type: HVACEquipmentType;
  size: { value: string; unit: string };
  efficiencies: { value: string; unit: string }[];
  quantity: number;
}

export interface HVACAccessoryItem extends Omit<HVACEquipmentItem, 'type'> {
  type: HVACAccessoryType;
  description?: string;
  /** Only set on `airFilter` items; requiredness there is enforced by validation, not the type. */
  airFilterType?: HVACAirFilterType;
}

export interface HVACPreliminaryAccessoryItem {
  type: HVACAccessoryType;
  quantity: number;
  description?: string;
  /** Only set on `airFilter` items; requiredness there is enforced by validation, not the type. */
  airFilterType?: HVACAirFilterType;
}

export interface HVACZoningItem extends Omit<HVACEquipmentItem, 'type' | 'manufacturer' | 'model'> {
  type: HVACAccessoryType.zoning | ZoningType;
  description?: string;
  manufacturer?: string;
  model?: string;
}

export interface HVACPreliminaryZoningItem {
  type: HVACAccessoryType.zoning;
  quantity: number;
  description?: string;
}

export interface HVACSitePrepItem {
  name: string;
}

export interface HVACDiscountItem {
  name: string;
}

/** @deprecated */
export enum LegacyHVACSystemCategory {
  'Conventional Ducted Split System' = 'Conventional Ducted Split System',
  'Conventional Ducted System' = 'Conventional Ducted System',
  Package = 'Package',
  'Ductless Mini Split' = 'Ductless Mini Split',
  'Ducted Mini Split' = 'Ducted Mini Split',
}

export enum HVACSystemCategory {
  'Heat Pump Split System' = 'Heat Pump Split System',
  'Gas Conventional Split System' = 'Gas Conventional Split System',
  'Dual Fuel Split System' = 'Dual Fuel Split System',
  'Packaged AC' = 'Packaged AC',
  'Furnace Only' = 'Furnace Only',
  'Packaged Heat Pump' = 'Packaged Heat Pump',
  'Packaged Gas/Electric' = 'Packaged Gas/Electric',
  'Packaged Dual Fuel' = 'Packaged Dual Fuel',
  'Single Zone Mini Split' = 'Single Zone Mini Split',
  'Multi Zone Mini Split' = 'Multi Zone Mini Split',
  'Air Conditioning/Air Handler Split System' = 'Air Conditioning/Air Handler Split System',
}

export const HVACSystemCategoryDefaultEquipmentTypes: Record<AllHVACSystemCategories, HVACEquipmentType[]> = {
  'Heat Pump Split System': [HVACEquipmentType.airHandler, HVACEquipmentType.heatPump, HVACEquipmentType.heatStrip],
  'Gas Conventional Split System': [
    HVACEquipmentType.furnace,
    HVACEquipmentType.evaporatorCoil,
    HVACEquipmentType.airConditioner,
  ],
  'Dual Fuel Split System': [
    HVACEquipmentType.furnace,
    HVACEquipmentType.evaporatorCoil,
    HVACEquipmentType.heatPump,
  ],
  'Packaged AC': [HVACEquipmentType.packagedAC],
  'Packaged Heat Pump': [HVACEquipmentType.packagedHeatPump, HVACEquipmentType.heatStrip],
  'Packaged Gas/Electric': [HVACEquipmentType.packagedGasAC],
  'Packaged Dual Fuel': [HVACEquipmentType.packagedDualFuel],
  'Single Zone Mini Split': [HVACEquipmentType.ductlessIndoor, HVACEquipmentType.ductlessOutdoor],
  'Multi Zone Mini Split': [HVACEquipmentType.ductlessIndoor, HVACEquipmentType.ductlessOutdoor],
  'Conventional Ducted Split System': [],
  'Conventional Ducted System': [],
  Package: [],
  'Ductless Mini Split': [],
  'Ducted Mini Split': [],
  'Air Conditioning/Air Handler Split System': [
    HVACEquipmentType.airConditioner,
    HVACEquipmentType.airHandler,
    HVACEquipmentType.heatStrip,
  ],
  'Furnace Only': [HVACEquipmentType.furnace],
};

export type AllHVACSystemCategories = LegacyHVACSystemCategory | HVACSystemCategory;

/** Mini-splits carry no accessories — the Comfort Plan covers their major equipment outright. */
export const MINI_SPLIT_SYSTEM_CATEGORIES: readonly AllHVACSystemCategories[] = [
  HVACSystemCategory['Single Zone Mini Split'],
  HVACSystemCategory['Multi Zone Mini Split'],
  LegacyHVACSystemCategory['Ductless Mini Split'],
  LegacyHVACSystemCategory['Ducted Mini Split'],
];

export const isMiniSplitSystemCategory = (systemCategory: string | undefined | null): boolean =>
  !!systemCategory && MINI_SPLIT_SYSTEM_CATEGORIES.includes(systemCategory as AllHVACSystemCategories);

export const HVACEquipmentSizeEfficiencyMap: Partial<
  Record<
    keyof typeof HVACEquipmentType,
    { size: { unit: string; values: string[] }; efficiencies: { unit: string; values: string[] }[] }
  >
> = {
  [HVACEquipmentType.airHandler]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [],
  },
  [HVACEquipmentType.heatPump]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [{ unit: 'SEER2', values: ['14-16', '16-18', '18-23', '23+'] }],
  },
  [HVACEquipmentType.furnace]: {
    size: { unit: 'BTU', values: ['40k-60k', '60k-80k', '80k-100k', '100k-120k', '120k+'] },
    efficiencies: [{ unit: 'AFUE%', values: ['80%', '90% +'] }],
  },
  [HVACEquipmentType.evaporatorCoil]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [],
  },
  [HVACEquipmentType.ductlessOutdoor]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '6,000 / .5',
        '9,000 / .75',
        '12,000 / 1',
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },

    efficiencies: [
      { unit: 'SEER2', values: ['14-16', '16-18', '18-23', '23+'] },
      { unit: 'HSPF2', values: ['7-8.5', '8.5-10', '10+'] },
    ],
  },
  [HVACEquipmentType.ductlessIndoor]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '6,000 / .5',
        '9,000 / .75',
        '12,000 / 1',
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },

    efficiencies: [],
  },
  [HVACEquipmentType.airConditioner]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [{ unit: 'SEER2', values: ['13.4-14', '14-16', '16-18', '18-23', '23+'] }],
  },
  [HVACEquipmentType.packagedAC]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [{ unit: 'SEER2', values: ['13.4-14', '14-16', '16-18', '18-23', '23+'] }],
  },
  [HVACEquipmentType.packagedHeatPump]: {
    size: {
      unit: 'BTU/Ton',
      values: ['24,000 / 2', '30,000 / 2.5', '36,000 / 3', '42,000 / 3.5', '48,000 / 4', '60,000 / 5'],
    },
    efficiencies: [
      { unit: 'SEER2', values: ['13.4-14', '14-16'] },
      { unit: 'HSPF2', values: ['7-8'] },
    ],
  },
  [HVACEquipmentType.packagedGasAC]: {
    size: {
      unit: 'BTU/Ton',
      values: ['24,000 / 2', '30,000 / 2.5', '36,000 / 3', '42,000 / 3.5', '48,000 / 4', '60,000 / 5'],
    },
    efficiencies: [
      { unit: 'SEER2', values: ['13.4-14', '14-16'] },
      { unit: 'AFUE%', values: ['80%'] },
    ],
  },
  [HVACEquipmentType.packagedDualFuel]: {
    size: {
      unit: 'BTU/Ton',
      values: [
        '18,000 / 1.5',
        '24,000 / 2',
        '30,000 / 2.5',
        '36,000 / 3',
        '42,000 / 3.5',
        '48,000 / 4',
        '60,000 / 5',
      ],
    },
    efficiencies: [
      { unit: 'SEER2', values: ['13.4-14', '14-16'] },
      { unit: 'AFUE%', values: ['80%'] },
      { unit: 'HSPF2', values: ['7-9'] },
    ],
  },
  [HVACEquipmentType.heatStrip]: {
    size: { unit: 'kW', values: ['5-7.5', '10-15', '20-25', '30'] },
    efficiencies: [],
  },
};

export interface HVACSystem {
  systemCategory: AllHVACSystemCategories;
  conditionedArea?: number;
  name?: string;
  equipment: { items: HVACEquipmentItem[]; totalCost?: number };
  accessories?: { items: HVACAccessoryItem[]; totalCost?: number };
  zoning?: { items: HVACZoningItem[]; totalCost?: number };
  sitePrep?: { items: HVACSitePrepItem[]; totalCost?: number };
  discounts?: { items: HVACDiscountItem[]; totalCost?: number };
  ahriNumber?: string;
  manufacturer?: string;
  /**
   * The QC-selected system manufacturer as structured AVL data, alongside the flat `manufacturer`
   * string that every downstream consumer still reads. Ids are the stable reporting key; the names
   * are snapshotted at save time so a historical package stays self-describing after a rename or a
   * deactivation, which is safe because `maybeCreateHVACSystemDesign` always inserts a new design
   * rather than mutating a prior one.
   */
  systemManufacturer?: {
    offAvl: boolean;
    offAvlName?: string;
    manufacturerId?: ObjectId;
    manufacturerName?: string;
    brandId?: ObjectId;
    brandName?: string;
  };
  efficiencies?: { seer2: string; hspf2?: string };
}

export type HVACPreliminarySystem = Pick<
  HVACSystem,
  'systemCategory' | 'conditionedArea' | 'name' | 'sitePrep' | 'discounts'
> & {
  equipment: { items: HVACPreliminaryEquipmentItem[]; totalCost?: number };
  accessories?: { items: HVACPreliminaryAccessoryItem[]; totalCost?: number };
  zoning?: { items: HVACPreliminaryZoningItem; totalCost?: number };
};

export interface BaseHVACSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
}

export type HVACSystemDesign = BaseHVACSystemDesign &
  ({ isPreliminary: true; systems: HVACPreliminarySystem[] } | { isPreliminary?: false; systems: HVACSystem[] });

// ==================== Water Heater Types ====================

export enum WaterHeaterEquipmentType {
  waterHeater = 'waterHeater',
}

export enum WaterHeaterFuelType {
  electric = 'electric',
  gas = 'gas',
}

export enum WaterHeaterType {
  tank = 'tank',
  tankless = 'tankless',
  heatPump = 'heatPump',
}

export const WaterHeaterEquipmentTypeLabels: Record<WaterHeaterEquipmentType, string> = {
  [WaterHeaterEquipmentType.waterHeater]: 'Water Heater',
} as const;

export const WaterHeaterFuelTypeLabels: Record<WaterHeaterFuelType, string> = {
  [WaterHeaterFuelType.electric]: 'Electric',
  [WaterHeaterFuelType.gas]: 'Gas',
} as const;

export const WaterHeaterTypeLabels: Record<WaterHeaterType, string> = {
  [WaterHeaterType.tank]: 'Tank',
  [WaterHeaterType.tankless]: 'Tankless',
  [WaterHeaterType.heatPump]: 'Heat Pump',
} as const;

export const WaterHeaterTankCapacityRanges = [
  { value: '20-30', label: '20-30 gallons' },
  { value: '30-40', label: '30-40 gallons' },
  { value: '40-50', label: '40-50 gallons' },
  { value: '50-66', label: '50-66 gallons' },
  { value: '66-80', label: '66-80 gallons' },
  { value: '80-100', label: '80-100 gallons' },
  { value: '100-120', label: '100-120 gallons' },
] as const;

export const WaterHeaterTanklessFlowRateRanges = [
  { value: 'under-4', label: 'Under 4 GPM' },
  { value: '4-6', label: '4-6 GPM' },
  { value: '6-8', label: '6-8 GPM' },
  { value: '8-10', label: '8-10 GPM' },
  { value: '10+', label: '10+ GPM' },
] as const;

export interface WaterHeaterEquipmentItem {
  type: WaterHeaterEquipmentType;
  manufacturer: string;
  model: string;
  quantity: number;
  serialNumbers?: string[];
  fuelType: WaterHeaterFuelType;
  waterHeaterType: WaterHeaterType;
  tankCapacity?: { value: string; unit: string };
  tanklessFlowRate?: { value: string; unit: string };
}

export interface WaterHeaterPreliminaryEquipmentItem {
  type: WaterHeaterEquipmentType;
  quantity: number;
  fuelType: WaterHeaterFuelType;
  waterHeaterType: WaterHeaterType;
  tankCapacity?: { value: string; unit: string };
  tanklessFlowRate?: { value: string; unit: string };
}

export interface WaterHeaterSitePrepItem {
  name: string;
}

export interface WaterHeaterDiscountItem {
  name: string;
}

export interface WaterHeaterSystem {
  name?: string;
  equipment: { items: WaterHeaterEquipmentItem[]; totalCost?: number };
  sitePrep?: { items: WaterHeaterSitePrepItem[]; totalCost?: number };
  discounts?: { items: WaterHeaterDiscountItem[]; totalCost?: number };
  ahriNumber?: string;
}

export type WaterHeaterPreliminarySystem = Pick<WaterHeaterSystem, 'name' | 'sitePrep' | 'discounts'> & {
  equipment: { items: WaterHeaterPreliminaryEquipmentItem[]; totalCost?: number };
};

export interface BaseWaterHeaterSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
}

export type WaterHeaterSystemDesign = BaseWaterHeaterSystemDesign &
  (
    | { isPreliminary: true; systems: WaterHeaterPreliminarySystem[] }
    | { isPreliminary?: false; systems: WaterHeaterSystem[] }
  );

// ==================== End Water Heater Types ====================

// ==================== Generator Types ====================

export enum GeneratorEquipmentType {
  generator = 'generator',
}

export enum GeneratorFuelType {
  naturalGas = 'naturalGas',
  propane = 'propane',
}

export const GeneratorEquipmentTypeLabels: Record<GeneratorEquipmentType, string> = {
  [GeneratorEquipmentType.generator]: 'Generator',
} as const;

export const GeneratorFuelTypeLabels: Record<GeneratorFuelType, string> = {
  [GeneratorFuelType.naturalGas]: 'Natural Gas (NG)',
  [GeneratorFuelType.propane]: 'Propane (LP)',
} as const;

export interface GeneratorEquipmentItem {
  type: GeneratorEquipmentType;
  manufacturer: string;
  model: string;
  quantity: number;
  serialNumbers?: string[];
  fuelType: GeneratorFuelType;
  ratedKw?: number;
  runningKw?: number;
  // Power ratings sourced from the product catalog (attributes power-rating-lp-kw / power-rating-gas-kw)
  lpPowerRating?: string;
  gasPowerRating?: string;
}

export interface GeneratorPreliminaryEquipmentItem {
  type: GeneratorEquipmentType;
  quantity: number;
  fuelType: GeneratorFuelType;
  ratedKw?: number;
}

// NOTE: Site prep, accessories, and discounts are temporarily commented out for the generator
// program. They may return, so the definitions are left in place rather than deleted.
// export interface GeneratorSitePrepItem {
//   name: string;
// }

// export interface GeneratorAccessoryItem {
//   name: string;
// }

// export interface GeneratorDiscountItem {
//   name: string;
// }

export interface GeneratorSystem {
  name?: string;
  equipment: { items: GeneratorEquipmentItem[]; totalCost?: number };
  // sitePrep?: { items: GeneratorSitePrepItem[]; totalCost?: number };
  // accessories?: { items: GeneratorAccessoryItem[]; totalCost?: number };
  // discounts?: { items: GeneratorDiscountItem[]; totalCost?: number };
}

export interface BaseGeneratorSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
}

// Generator designs are always final (a model is required); there is no preliminary variant.
export type GeneratorSystemDesign = BaseGeneratorSystemDesign & {
  isPreliminary?: false;
  systems: GeneratorSystem[];
};

// ==================== End Generator Types ====================

// ==================== Geothermal Types ====================

export enum GeothermalLoopType {
  verticalBore = 'verticalBore',
  horizontalTrench = 'horizontalTrench',
}

export const GeothermalLoopTypeLabels: Record<GeothermalLoopType, string> = {
  [GeothermalLoopType.verticalBore]: 'Vertical',
  [GeothermalLoopType.horizontalTrench]: 'Horizontal',
};

export enum GeothermalIndoorUnitType {
  forcedAirPackaged = 'forcedAirPackaged',
  forcedAirSplit = 'forcedAirSplit',
}

export const GeothermalIndoorUnitTypeLabels: Record<GeothermalIndoorUnitType, string> = {
  [GeothermalIndoorUnitType.forcedAirPackaged]: 'Forced Air - Packaged',
  [GeothermalIndoorUnitType.forcedAirSplit]: 'Forced Air - Split',
};

export enum GeothermalEquipmentType {
  packagedHeatPump = 'packagedHeatPump',
  heatPump = 'heatPump',
  airHandler = 'airHandler',
}
export const GeothermalEquipmentTypeLabels: Record<GeothermalEquipmentType, string> = {
  [GeothermalEquipmentType.packagedHeatPump]: 'Packaged Heat Pump',
  [GeothermalEquipmentType.heatPump]: 'Heat Pump',
  [GeothermalEquipmentType.airHandler]: 'Air Handler',
};

export const GeothermalIndoorUnitTypeDefaultEquipmentTypes: Record<
  GeothermalIndoorUnitType,
  GeothermalEquipmentType[]
> = {
  [GeothermalIndoorUnitType.forcedAirPackaged]: [GeothermalEquipmentType.packagedHeatPump],
  [GeothermalIndoorUnitType.forcedAirSplit]: [GeothermalEquipmentType.heatPump, GeothermalEquipmentType.airHandler],
};

export const GeothermalCapacityTonsOptions = [0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6] as const;
export type GeothermalCapacityTons = (typeof GeothermalCapacityTonsOptions)[number];

export interface GeothermalEquipmentItem {
  manufacturer: string;
  model?: string;
  description?: string;
  quantity: number;
  type: GeothermalEquipmentType;
  serialNumber?: string;
}

export interface BaseGeothermalSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
  isPreliminary?: boolean;
}

export interface GeothermalSystem {
  name?: string;
  // Loop config
  loopType: GeothermalLoopType;
  // System config
  indoorUnitType: GeothermalIndoorUnitType;
  capacityTons: GeothermalCapacityTons;
  // Features
  dhwAssist: boolean;
  auxiliaryHeat: boolean;
  auxiliaryHeatType?: string;
  // Cost
  leaseAmount: number;
  manufacturer: GeothermalManufacturer;
  drillingCost?: number; // informational only, does NOT drive payout
  // Equipment (populated post-install)
  equipment: GeothermalEquipmentItem[];
  // Monitoring (populated post-install, one ID per system regardless of split/packaged config)
  monitoringId?: string;
}

export interface GeothermalDesignDocumentsEquipmentDetail {
  type: GeothermalEquipmentType;
  model: string;
  description?: string;
}

export interface GeothermalDesignDocumentsSystemDetail {
  equipment: GeothermalDesignDocumentsEquipmentDetail[];
}

export interface GeothermalDesignDocumentsSystemDetails {
  systems: GeothermalDesignDocumentsSystemDetail[];
}

export interface GeothermalSystemDesign extends BaseGeothermalSystemDesign {
  ecEligible?: boolean;
  // Computed totals (sum of per-system values)
  totalLeaseAmount: number;
  totalDrillingCost?: number;
  // Systems
  systems: GeothermalSystem[];
}

// ==================== End Geothermal Types ====================

export interface BaseEnergyBackupSystemDesign {
  accountId: ObjectId;
  isCurrent: boolean;
}

export interface EquipmentModel {
  modelNumber?: string;
  name: string;
  quantity: number;
  productId?: string;
  capacity: number;
}
export interface EnergyBackupSystem {
  type: EquipmentType;
  productId: string;
  manufacturer: string;
  models: EquipmentModel[];
  totalCost: number;
}

export type EnergyBackupSystemDesign = BaseEnergyBackupSystemDesign & {
  equipment: EnergyBackupSystem[];
};

export type SystemDesignReturnType<T extends ProgramType> = T extends ProgramType.hvac
  ? HVACSystemDesign
  : T extends ProgramType.energyBackup
    ? EnergyBackupSystemDesign
    : T extends ProgramType.waterHeater
      ? WaterHeaterSystemDesign
      : T extends ProgramType.generator
        ? GeneratorSystemDesign
        : T extends ProgramType.geothermal
          ? GeothermalSystemDesign
          : SystemDesign;

export enum DomesticContentMountingHardwareType {
  fastenerClamp = 'fastenerClamp',
  fastenerSplice = 'fastenerSplice',
  fastenerMlpe = 'fastenerMlpe',
  rail = 'rail',
}

export const DomesticContentMountingHardwareLabels: Record<DomesticContentMountingHardwareType, string> = {
  [DomesticContentMountingHardwareType.rail]: 'Rail',
  [DomesticContentMountingHardwareType.fastenerClamp]: 'Fastener - Clamp',
  [DomesticContentMountingHardwareType.fastenerSplice]: 'Fastener - Splice',
  [DomesticContentMountingHardwareType.fastenerMlpe]: 'Fastener - MLPE',
} as const;
